import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAdminCourses, useCreateEnrollment } from "@/hooks/useAdminData";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const enrollmentSchema = z.object({
	user_email: z.string().email("Valid email is required"),
	course_id: z.string().min(1, "Course is required"),
	note: z.string().optional(),
});

type EnrollmentFormValues = z.infer<typeof enrollmentSchema>;

export function AdminEnrollmentNew() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: coursesData, isLoading: coursesLoading } = useAdminCourses();
	const mutation = useCreateEnrollment();

	const courses = coursesData?.courses ?? [];

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<EnrollmentFormValues>({
		resolver: zodResolver(enrollmentSchema),
		defaultValues: {
			user_email: "",
			course_id: "",
			note: "",
		},
	});

	function onSubmit(values: EnrollmentFormValues) {
		mutation.mutate(
			{
				user_email: values.user_email,
				course_id: values.course_id,
				note: values.note || undefined,
			},
			{
				onSuccess: () => {
					toast.success(t("admin.enrollmentCreated"));
					navigate("/admin/students");
				},
				onError: (error) => {
					if (error.message?.includes("not found")) {
						toast.error(t("admin.studentNotFound"));
					} else {
						toast.error(t("admin.saveFailed"));
					}
				},
			},
		);
	}

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[
					{ label: t("admin.dashboard"), href: "/admin" },
					{ label: t("admin.newEnrollment") },
				]}
			/>

			<h1 className="text-3xl font-bold text-primary">{t("admin.newEnrollment")}</h1>

			<form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl space-y-6">
				<div className="space-y-2">
					<Label htmlFor="user_email">{t("admin.studentEmail")}</Label>
					<Input
						id="user_email"
						type="email"
						{...register("user_email")}
						placeholder="student@example.com"
					/>
					{errors.user_email && <p className="text-sm text-error">{errors.user_email.message}</p>}
				</div>

				<div className="space-y-2">
					<Label>{t("admin.selectCourse")}</Label>
					<Controller
						control={control}
						name="course_id"
						render={({ field }) => (
							<Select value={field.value} onValueChange={field.onChange} disabled={coursesLoading}>
								<SelectTrigger>
									<SelectValue placeholder={t("admin.selectCoursePlaceholder")} />
								</SelectTrigger>
								<SelectContent>
									{courses.map((course) => (
										<SelectItem key={course.id} value={course.id}>
											{course.title}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					/>
					{errors.course_id && <p className="text-sm text-error">{errors.course_id.message}</p>}
				</div>

				<div className="space-y-2">
					<Label htmlFor="note">{t("admin.note")}</Label>
					<Textarea
						id="note"
						{...register("note")}
						placeholder={t("admin.notePlaceholder")}
						rows={3}
					/>
				</div>

				<div className="flex gap-3">
					<Button type="submit" disabled={mutation.isPending}>
						{t("admin.createEnrollment")}
					</Button>
					<Button type="button" variant="outline" onClick={() => navigate("/admin/students")}>
						{t("admin.cancel")}
					</Button>
				</div>
			</form>
		</div>
	);
}
